import {
  AuthorizeCard, Van, InStorePickup, ShipToHome, Store, Calendar
} from '@one-thd/sui-icons';
import { FULFILLMENT_TYPES, STATUS_CODES } from '../constants';

export const getProgressBarData = (fulfillmentType: string) => {
  switch (fulfillmentType) {
  case FULFILLMENT_TYPES.SHIP_TO_HOME:
    return [
      { icon: AuthorizeCard, title: 'Processed', subtitle: '' },
      { icon: Van, title: 'Shipped', subtitle: '' },
      { icon: Van, title: 'Out For Delivery', subtitle: '' },
      { icon: ShipToHome, title: 'Delivered' },
    ];

  case FULFILLMENT_TYPES.BUY_ONLINE_PICKUP_IN_STORE:
  case FULFILLMENT_TYPES.WILL_CALL_FULFILLMENT:
    return [
      { icon: AuthorizeCard, title: 'Processed', subtitle: '' },
      { icon: Store, title: 'Ready for Pickup', subtitle: '' },
      { icon: InStorePickup, title: 'Picked Up', subtitle: '' },
    ];
  case FULFILLMENT_TYPES.BUY_ONLINE_SHIP_TO_STORE:
    return [
      { icon: AuthorizeCard, title: 'Processed', subtitle: '' },
      { icon: Van, title: 'Shipped', subtitle: '' },
      { icon: Store, title: 'Ready for Pickup', subtitle: '' },
      { icon: InStorePickup, title: 'Picked Up', subtitle: '' },
    ];
  case FULFILLMENT_TYPES.BUY_ONLINE_DELIVER_FROM_STORE:
    return [
      { icon: AuthorizeCard, title: 'Processed', subtitle: '' },
      { icon: Van, title: 'Shipped', subtitle: '' },
      { icon: Van, title: 'Out for Delivery', subtitle: '' },
      { icon: ShipToHome, title: 'Delivered', subtitle: '' },
    ];
  case FULFILLMENT_TYPES.APPLIANCE:
    return [
      { icon: AuthorizeCard, title: 'Processed', subtitle: '' },
      { icon: Calendar, title: 'Delivery Window Scheduled', subtitle: '' },
      { icon: Van, title: 'Out for Delivery', subtitle: '' },
      { icon: ShipToHome, title: 'Delivered', subtitle: '' },
    ];
  default:
    return [];
  }
};

/**
 * Maps an order status code to an index on the progress bar being rendered.
 *
 * The bars differ in length — BOPIS and Will Call have three steps, every other
 * type has four — so one flat table cannot serve them. `getActiveSteps(131)`
 * used to return 1 for every type, which is Ready for Pickup on the BOPIS bar
 * but Shipped on the BOSS bar.
 *
 * That was unreachable while `getFulfillmentType` collapsed everything to BOPIS
 * or ship-to-home. Reading the granular `fulfillmentType` off `orderDetails`
 * makes BOSS renderable, so the table is per type.
 *
 * Called without a fulfillment type it falls back to this flat table, kept for
 * callers this plan has not yet updated — with 132 corrected from the old 2 to
 * 0, because 132 means the order is being prepared at the store, and 2 is
 * "Picked Up" on the three-step pickup bar. That old mapping would have shown
 * an order still being prepared as already collected.
 */
const LEGACY_STEP_BY_STATUS: { [code: number]: number } = {
  100: 0,
  101: 0,
  102: 2,
  103: 2,
  104: 2,
  105: 3,
  106: 0,
  108: 2,
  109: 2,
  110: 2,
  130: 1,
  131: 1,
  132: 0,
  200: 3,
};

/**
 * An index no step sits on, so the bar highlights nothing.
 *
 * A canceled or returned order is not partway along its bar, and the drawer
 * header already says CANCELED — highlighting Processed underneath it reads as
 * progress the order is not making.
 */
const NO_ACTIVE_STEP = -1;

/** The terminal codes that take an order off its bar rather than along it. */
const OFF_BAR_STEPS: { [code: number]: number } = {
  [STATUS_CODES.CANCELED]: NO_ACTIVE_STEP,
  [STATUS_CODES.WORK_ORDER_CANCELED]: NO_ACTIVE_STEP,
  [STATUS_CODES.RETURNED]: NO_ACTIVE_STEP,
  [STATUS_CODES.RETURN_IN_PROGRESS]: NO_ACTIVE_STEP,
};

/** Processed, Ready for Pickup, Picked Up. */
const PICKUP_STEPS: { [code: number]: number } = {
  ...OFF_BAR_STEPS,
  [STATUS_CODES.SEE_ORDER_DETAILS]: 0,
  [STATUS_CODES.PROCESSING]: 0,
  [STATUS_CODES.BACKORDERED]: 0,
  [STATUS_CODES.ON_ITS_WAY_TO_STORE]: 0,
  [STATUS_CODES.PREPARING_FOR_PICKUP]: 0,
  [STATUS_CODES.READY_FOR_PICKUP]: 1,
  [STATUS_CODES.COMPLETED]: 2,
};

/** Processed, Shipped, Ready for Pickup, Picked Up. */
const SHIP_TO_STORE_STEPS: { [code: number]: number } = {
  ...OFF_BAR_STEPS,
  [STATUS_CODES.SEE_ORDER_DETAILS]: 0,
  [STATUS_CODES.PROCESSING]: 0,
  [STATUS_CODES.BACKORDERED]: 0,
  [STATUS_CODES.SHIPPED]: 1,
  [STATUS_CODES.IN_TRANSIT]: 1,
  [STATUS_CODES.ON_ITS_WAY_TO_STORE]: 1,
  [STATUS_CODES.PREPARING_FOR_PICKUP]: 1,
  [STATUS_CODES.READY_FOR_PICKUP]: 2,
  [STATUS_CODES.COMPLETED]: 3,
};

/** Processed, Shipped, Out for Delivery, Delivered. */
const DELIVERY_STEPS: { [code: number]: number } = {
  ...OFF_BAR_STEPS,
  [STATUS_CODES.SEE_ORDER_DETAILS]: 0,
  [STATUS_CODES.PROCESSING]: 0,
  [STATUS_CODES.BACKORDERED]: 0,
  [STATUS_CODES.SHIPPED]: 1,
  [STATUS_CODES.IN_TRANSIT]: 1,
  [STATUS_CODES.ATTEMPTED]: 2,
  [STATUS_CODES.OUT_FOR_DELIVERY]: 2,
  [STATUS_CODES.DELIVERY_ATTEMPTED]: 2,
  [STATUS_CODES.DELIVERED]: 3,
  [STATUS_CODES.COMPLETED]: 3,
};

/** Processed, Delivery Window Scheduled, Out for Delivery, Delivered. */
const APPLIANCE_STEPS: { [code: number]: number } = {
  ...OFF_BAR_STEPS,
  [STATUS_CODES.SEE_ORDER_DETAILS]: 0,
  [STATUS_CODES.PROCESSING]: 0,
  [STATUS_CODES.BACKORDERED]: 0,
  [STATUS_CODES.MAJOR_APPLIANCE_ON_SCHEDULE]: 1,
  [STATUS_CODES.ATTEMPTED]: 2,
  [STATUS_CODES.OUT_FOR_DELIVERY]: 2,
  [STATUS_CODES.DELIVERY_ATTEMPTED]: 2,
  [STATUS_CODES.DELIVERED]: 3,
  [STATUS_CODES.COMPLETED]: 3,
};

const STEPS_BY_FULFILLMENT_TYPE: { [type: string]: { [code: number]: number } } = {
  [FULFILLMENT_TYPES.BUY_ONLINE_PICKUP_IN_STORE]: PICKUP_STEPS,
  [FULFILLMENT_TYPES.WILL_CALL_FULFILLMENT]: PICKUP_STEPS,
  [FULFILLMENT_TYPES.BUY_ONLINE_SHIP_TO_STORE]: SHIP_TO_STORE_STEPS,
  [FULFILLMENT_TYPES.SHIP_TO_HOME]: DELIVERY_STEPS,
  [FULFILLMENT_TYPES.BUY_ONLINE_DELIVER_FROM_STORE]: DELIVERY_STEPS,
  [FULFILLMENT_TYPES.APPLIANCE]: APPLIANCE_STEPS,
};

export const getActiveSteps = (orderStatus: number, fulfillmentType?: string) => {
  const stepMapping = fulfillmentType
    ? STEPS_BY_FULFILLMENT_TYPE[fulfillmentType]
    : LEGACY_STEP_BY_STATUS;

  return (stepMapping ?? LEGACY_STEP_BY_STATUS)[orderStatus] ?? 0;
};
